import classNames from 'classnames'

import { CopyWithText, ImageComponent } from '@apps-common/components/common'
import { VertSpacerLg, VertSpacerXS } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_ACTIVATION_ALLOWED_PRODUCTS } from '@apps-common/config/product-codes'
import {
  unitIsActivePortal,
  unitIsCancelledPortal,
  unitIsInactivePortal,
  unitIsPendingCancellationPortal
} from '@apps-common/helpers/unit-helpers'
import { IAppNavigationOptions } from '@apps-common/hooks/useNavigation'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'
import { ROUTE_ORDER_DETAILS, ROUTE_UNIT_ACTIVATION_DETAILS, ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

import { ButtonRoundSm } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { PortalUnitDetailsModal } from '../../../modals/PortalUnitDetailsModal'
import { navigateActivationWithUnitDetails } from '../../../pages/activations/navigateActivationWithUnitDetails'

interface IProps {
  bgClasses: string
  textClasses: string
  iconClasses: string
  bgSecondaryClasses: string
  textSecondaryClasses: string
  bgTertiaryClasses: string
  unit: IPortalUnitSearchModel
  buttonText?: string
  replacementButton?: any
  hideOrderButton?: boolean
  hideDetailsButton?: boolean
  navigateForward: (opts: IAppNavigationOptions) => void
  callback?: (unit: IPortalUnitSearchModel) => void
}

export const PortalUnitListCard: React.FC<IProps> = (props) => {
  const {
    bgClasses,
    textClasses,
    bgSecondaryClasses,
    textSecondaryClasses,
    unit,
    buttonText,
    replacementButton,
    hideOrderButton,
    hideDetailsButton,
    navigateForward,
    callback
  } = props

  const {
    status,
    unitId,
    productId,
    accountNo,
    mobileNo,
    imei,
    serialNo,
    monitoringCentre,
    bureau,
    productImageUrl,
    siteName,
    pendingCancellationDate,
    orderId,
    cancellationProcessed,
    cancellationDate,
    unitActivationRequestID
  } = unit

  const isInactive = unitIsInactivePortal(unit)
  const isActive = unitIsActivePortal(unit)
  const isPendingCancellation = unitIsPendingCancellationPortal(unit)
  const isCancelled = unitIsCancelledPortal(unit)
  const isActiveLike = isActive || isPendingCancellation
  const variant = isInactive ? 'inactive' : isCancelled ? 'cancelled' : 'active'
  const showInactive = variant === 'inactive'
  const showCancelled = variant === 'cancelled'
  const showActive = variant === 'active'
  const mcName = monitoringCentre || 'Monitoring Centre'
  const mcLabel = showInactive ? '<TBC>' : `${mcName}${bureau === mcName ? '' : ` | ${bureau}`}`

  const handleManage = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (callback) {
      callback(unit)
    } else {
      navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(status, unitId), route: ROUTE_UNIT_MANAGE })
    }
  }

  const handleOrder = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (orderId) {
      navigateForward({ routeUrl: ROUTE_ORDER_DETAILS.buildPath(orderId), route: ROUTE_ORDER_DETAILS })
    }
  }

  const handleActivation = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (unitActivationRequestID) {
      navigateForward({
        routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(unitActivationRequestID),
        route: ROUTE_UNIT_ACTIVATION_DETAILS
      })
    } else {
      navigateActivationWithUnitDetails(navigateForward, unitId, serialNo, imei)
    }
  }

  return (
    <div className={classNames('rounded-[35px] p-5 min-w-[160px] overflow-hidden border border-border text-left', bgClasses)}>
      <div className='flex items-stretch justify-between gap-[10px] h-full'>
        <div className='grow'>
          <div className='flex items-center justify-start'>
            <h4
              className={classNames(
                'jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[5px]',
                textClasses
              )}
            >
              {mcLabel}
            </h4>
          </div>
          <div className='flex items-center justify-between gap-[10px]'>
            <div className='flex items-center justify-start gap-[5px]'>
              <div
                className={classNames(
                  'px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0',
                  bgSecondaryClasses
                )}
              >
                <commonIcons.processor className={textSecondaryClasses} />
                <h5 className={classNames('jost-light text-[0.85rem] uppercase', textClasses)}>
                  {showInactive ? <span className='text-[0.85em]'>TBC</span> : accountNo || <span className='text-[0.85em]'>None</span>}
                </h5>
              </div>
            </div>
            <div
              className={classNames(
                'w-[45px] h-[45px] rounded-[23px] flex items-center justify-center overflow-hidden',
                bgSecondaryClasses
              )}
            >
              <ImageComponent
                src={productImageUrl}
                alt={`Product ${accountNo}`}
                imageClasses={classNames('max-w-[30px]')}
                errorIconClasses='text-[1.2rem]'
              />
            </div>
          </div>
          <VertSpacerXS />

          {showInactive && (
            <div className='flex flex-col items-start justify-start pl-[5px] gap-[6px]'>
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={textClasses} />
              <CopyWithText textValue='S/N' value={serialNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
            </div>
          )}

          {showActive && (
            <div className='flex flex-col items-start justify-start gap-[6px]'>
              <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} className={textClasses} />
              <CopyWithText textValue='MOB' value={mobileNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              {isActiveLike && pendingCancellationDate && (
                <p
                  className={classNames(
                    'jost text-[0.75rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[7px]',
                    textClasses
                  )}
                >
                  <span className='bg-warning text-warning-content rounded-md px-[4px]'>Cancels on</span>{' '}
                  {toLocalTimestampStr(pendingCancellationDate, 'DD/MM/YYYY')}
                </p>
              )}
            </div>
          )}

          {showCancelled && (
            <div className='flex flex-col items-start justify-start gap-[5px] pl-[5px]'>
              <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} className={textClasses} />
              <CopyWithText textValue='MOB' value={mobileNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <p
                className={classNames(
                  'jost text-[0.75rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[6px]',
                  textClasses
                )}
              >
                <span className='bg-surface-2 text-text border border-border rounded-md px-[4px]'>Cancelled on</span>{' '}
                {toLocalTimestampStr(cancellationDate || '', 'DD/MM/YYYY')}
                <div
                  className={classNames(
                    'rounded-md px-[5px]',
                    cancellationProcessed && 'bg-error text-error-content',
                    !cancellationProcessed && 'bg-warning text-warning-content'
                  )}
                >
                  {cancellationProcessed ? 'Processed' : 'Pending'}
                </div>
              </p>
            </div>
          )}

          <VertSpacerLg />
          <div className='flex items-center justify-between gap-[10px] w-full'>
            <div className='flex items-stretch justify-evenly gap-[6px]'>
              {showInactive && !callback && UNIT_ACTIVATION_ALLOWED_PRODUCTS.includes(productId) && (
                <button
                  type='button'
                  className={classNames(
                    'relative flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full',
                    !unitActivationRequestID && 'action-success',
                    unitActivationRequestID && 'action-warning'
                  )}
                  onClick={handleActivation}
                >
                  <commonIcons.srActivations className='text-[0.9rem]' />
                </button>
              )}
              {replacementButton && replacementButton}
              {!replacementButton && (
                <>
                  {orderId && !hideOrderButton && (
                    <button
                      type='button'
                      aria-label='Device order'
                      className={classNames('action-success flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full')}
                      onClick={handleOrder}
                    >
                      <commonIcons.sbOrders className='text-[0.9rem]' />
                    </button>
                  )}
                  {!hideDetailsButton && (
                    <ModalButton
                      component={PortalUnitDetailsModal}
                      classes=''
                      componentProps={{
                        status,
                        unitId
                      }}
                      enterAnimation={modalSlideUpEnterAnimation}
                      leaveAnimation={modalSlideUpLeaveAnimation}
                    >
                      <ButtonRoundSm type='div' className='action-brand'>
                        <commonIcons.details />
                      </ButtonRoundSm>
                    </ModalButton>
                  )}
                </>
              )}
            </div>
            {!replacementButton && (
              <button
                type='button'
                className={classNames(
                  'action-brand min-w-[140px] flex items-center justify-between gap-[5px] jost text-[0.8rem] !pl-[15px] !p-[5px] !pr-[2px] h-[35px] rounded-full'
                )}
                onClick={handleManage}
              >
                {buttonText || 'Manage'}
                <div className='bg-brand text-on-brand w-[26px] h-[26px] rounded-[13px] flex items-center justify-center'>
                  <commonIcons.arrowAngled className='text-[0.9rem]' />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
